import { gsap } from 'gsap';

const COLUMN_SELECTOR = '[data-aside-animation-column]';
const TRACK_SELECTOR = '[data-aside-animation-track]';
const TOP_MARGIN_PERCENT = 5;
const BOTTOM_MARGIN_PERCENT = 5;

const createTrack = (column) => {
    const existingTrack = column.querySelector(TRACK_SELECTOR);
    if (existingTrack) {
        return existingTrack;
    }

    const svg = column.querySelector('svg');
    if (!svg) return null;

    const track = document.createElement('div');
    track.dataset.asideAnimationTrack = '1';
    track.style.display = 'flex';
    track.style.flexDirection = 'column';
    track.style.alignItems = 'stretch';
    track.style.willChange = 'transform';

    track.appendChild(svg);
    track.appendChild(svg.cloneNode(true));
    column.appendChild(track);

    return track;
};

const updateTrack = (track, direction, column) => {
    const firstSvg = track.querySelector('svg');
    if (!firstSvg) return;

    const height = firstSvg.getBoundingClientRect().height;
    if (!height) return;

    const topMargin = height * (TOP_MARGIN_PERCENT / 100);
    const bottomMargin = height * (BOTTOM_MARGIN_PERCENT / 100);
    const travel = height + bottomMargin;

    if (column) {
        column.style.height = `${height + topMargin + bottomMargin}px`;
    }

    track.style.gap = `${bottomMargin}px`;

    gsap.killTweensOf(track);

    const duration = 18;

    let tween;
    if (direction === 'down') {
        gsap.set(track, { y: topMargin - travel });
        tween = gsap.to(track, {
            y: topMargin,
            duration,
            ease: 'none',
            repeat: -1,
        });
    } else {
        gsap.set(track, { y: topMargin });
        tween = gsap.to(track, {
            y: topMargin - travel,
            duration,
            ease: 'none',
            repeat: -1,
        });
    }

    return tween;
};

export const asideAnimation = {
    init(root = document) {
        if (typeof window === 'undefined') return;

        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduceMotion) return;

        const sections = Array.from(root.querySelectorAll('[data-aside-animation]'));
        if (!sections.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;

                    const section = entry.target;
                    observer.unobserve(section);
                    this.play(section);
                }
            },
            {
                threshold: 0.2,
            },
        );

        for (const section of sections) {
            observer.observe(section);
        }
    },

    play(section) {
        if (!(section instanceof HTMLElement)) return;
        if (section.dataset.asideAnimationPlayed === '1') return;
        section.dataset.asideAnimationPlayed = '1';

        const columns = Array.from(section.querySelectorAll(COLUMN_SELECTOR));
        if (!columns.length) return;

        const tweens = [];

        for (const column of columns) {
            const direction = column.dataset.asideAnimationColumn === 'right' ? 'down' : 'up';
            const track = createTrack(column);
            if (!track) continue;

            const idx = tweens.length;
            const tween = updateTrack(track, direction, column);
            if (tween) tweens.push(tween);

            const resizeObserver = new ResizeObserver(() => {
                const wasPaused = tweens[idx]?.paused();
                const newTween = updateTrack(track, direction, column);
                if (newTween) {
                    if (wasPaused) newTween.pause();
                    tweens[idx] = newTween;
                }
            });

            resizeObserver.observe(column);
        }

        this.setupToggle(section, tweens);
    },

    setupToggle(section, tweens) {
        const btn = section.querySelector('[data-aside-animation-toggle]');
        if (!btn) return;

        const pauseEl = btn.querySelector('.aside-animation-toggle__pause');
        const playEl = btn.querySelector('.aside-animation-toggle__play');

        const update = (playing) => {
            pauseEl?.classList.toggle('hidden!', !playing);
            playEl?.classList.toggle('hidden!', playing);
            btn.setAttribute('aria-label', playing ? 'Pause animation' : 'Play animation');
        };

        btn.addEventListener('click', () => {
            const isPlaying = tweens.some(t => t.isActive() || !t.paused());
            if (isPlaying) {
                tweens.forEach(t => t.pause());
                update(false);
            } else {
                tweens.forEach(t => t.resume());
                update(true);
            }
        });
    },
};

if (typeof window !== 'undefined') {
    window.asideAnimation = asideAnimation;
}
